import React from 'react';
import { Link } from 'react-router-dom';

const HomePage = () => {
  const services = [
    {
      icon: 'code',
      title: 'Custom Development',
      description: 'Tailored software solutions built from the ground up to address your unique business challenges.'
    },
    {
      icon: 'cloud',
      title: 'Cloud & DevOps',
      description: 'Scalable infrastructure and automated pipelines that grow with your business.'
    },
    {
      icon: 'psychology',
      title: 'AI & Analytics',
      description: 'Intelligent systems that turn your data into actionable insights and competitive advantages.'
    },
    {
      icon: 'phone_iphone',
      title: 'Mobile Apps',
      description: 'Beautiful, performant applications that engage users on iOS and Android.'
    }
  ];

  const testimonials = [
    {
      quote: "Nurrevo transformed our legacy system into a modern platform that handles 10x the traffic. Their team became an extension of ours.",
      author: "Jennifer Walsh",
      role: "CTO, FinanceFlow",
      image: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop&crop=face"
    },
    {
      quote: "The AI solution they built has saved us over 2,000 hours per month in manual processing. ROI was achieved in just 3 months.",
      author: "Michael Torres",
      role: "VP Operations, MedCore",
      image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face"
    },
    {
      quote: "Professional, responsive, and incredibly skilled. They delivered ahead of schedule and under budget. Highly recommend.",
      author: "Sarah Kim",
      role: "Founder, ShopNext",
      image: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop&crop=face"
    }
  ];

  const caseStudies = [
    {
      image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&h=500&fit=crop',
      category: 'FinTech',
      title: 'Modernizing Banking Infrastructure',
      metric: '400%',
      metricLabel: 'Performance Increase'
    },
    {
      image: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1f?w=800&h=500&fit=crop',
      category: 'Healthcare',
      title: 'AI-Powered Diagnostic Platform',
      metric: '25%',
      metricLabel: 'Faster Diagnosis'
    }
  ];

  return (
    <main className="flex-grow">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10 bg-white/5"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
                We Build Software That 
                <span className="text-white/90"> Drives Growth</span>
              </h1>
              <p className="text-xl text-gray-300 leading-relaxed mb-8 max-w-xl">
                Partner with a team of senior engineers who turn complex challenges into elegant, scalable solutions. From startups to enterprises, we deliver technology that creates real business value.
              </p>
              <div className="flex flex-wrap gap-4 mb-12">
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center px-8 py-4 text-base font-semibold text-gray-900 bg-white rounded-xl shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200"
                >
                  Schedule a Consultation
                  <span className="material-symbols-outlined text-xl ml-2">arrow_forward</span>
                </Link>
                <Link
                  to="/case-studies"
                  className="inline-flex items-center justify-center px-8 py-4 text-base font-semibold text-white border border-white/20 rounded-xl hover:bg-white/10 transition-all duration-200"
                >
                  View Our Work
                </Link>
              </div>
              <div className="flex items-center gap-8 pt-8 border-t border-white/10">
                <div>
                  <div className="text-3xl font-bold text-white">98%</div>
                  <div className="text-sm text-gray-400">Client Retention</div>
                </div>
                <div className="w-px h-12 bg-white/20"></div>
                <div>
                  <div className="text-3xl font-bold text-white">150+</div>
                  <div className="text-sm text-gray-400">Projects Delivered</div>
                </div>
                <div className="w-px h-12 bg-white/20"></div>
                <div>
                  <div className="text-3xl font-bold text-white">12+</div>
                  <div className="text-sm text-gray-400">Years Experience</div>
                </div>
              </div>
            </div>
            <div className="hidden lg:block relative">
              <div className="aspect-square rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1553877522-43269d4ea984?w=800&h=800&fit=crop"
                  alt="Team collaboration"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white p-5 rounded-xl shadow-xl">
                <div className="flex items-center gap-4">
                  <div className="flex -space-x-2">
                    <img src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=40&h=40&fit=crop&crop=face" alt="" className="w-10 h-10 rounded-full border-2 border-white" />
                    <img src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=40&h=40&fit=crop&crop=face" alt="" className="w-10 h-10 rounded-full border-2 border-white" />
                    <img src="https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=40&h=40&fit=crop&crop=face" alt="" className="w-10 h-10 rounded-full border-2 border-white" />
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">40+ Engineers</div>
                    <div className="text-sm text-gray-500">Ready to help</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trusted By Section */}
      <section className="py-12 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-sm font-medium text-gray-500 mb-8">TRUSTED BY INDUSTRY LEADERS</p>
          <div className="flex flex-wrap justify-center items-center gap-x-12 gap-y-6 opacity-60 grayscale">
            {['Microsoft', 'Amazon', 'Stripe', 'Shopify', 'Salesforce', 'Adobe'].map((company, index) => (
              <div key={index} className="text-xl font-bold text-gray-900">{company}</div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 lg:py-28 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-end gap-8 mb-16">
            <div className="max-w-2xl">
              <span className="text-primary font-semibold text-sm uppercase tracking-wider">Our Expertise</span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-3 mb-4">
                Full-Stack Technology Solutions
              </h2>
              <p className="text-lg text-gray-600">
                We combine deep technical expertise with strategic thinking to deliver solutions that don't just work—they transform businesses.
              </p>
            </div>
            <Link
              to="/services"
              className="inline-flex items-center gap-2 text-primary font-semibold hover:gap-3 transition-all"
            >
              View All Services
              <span className="material-symbols-outlined">arrow_forward</span>
            </Link>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => (
              <div key={index} className="group bg-white p-8 rounded-2xl border border-gray-200 hover:border-gray-300 hover:shadow-lg transition-all duration-300">
                <div className="w-14 h-14 rounded-xl bg-gray-900 flex items-center justify-center text-white mb-6">
                  <span className="material-symbols-outlined text-2xl">{service.icon}</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                <p className="text-gray-600 leading-relaxed">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-primary font-semibold text-sm uppercase tracking-wider">Why Nurrevo</span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-3 mb-6">
                We're Not Just Developers. We're Growth Partners.
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-8">
                The difference between code that works and code that scales is experience. Our senior-only team has built software for Fortune 500 companies and unicorn startups alike.
              </p>
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center">
                    <span className="material-symbols-outlined text-gray-700 text-2xl">verified</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Senior Engineers Only</h4>
                    <p className="text-gray-600">No juniors learning on your dime. Every team member has 8+ years of production experience.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center">
                    <span className="material-symbols-outlined text-gray-700 text-2xl">schedule</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">On-Time, Every Time</h4>
                    <p className="text-gray-600">We've delivered 98% of projects on schedule. Your deadlines are our deadlines.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center">
                    <span className="material-symbols-outlined text-gray-700 text-2xl">support</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Ongoing Partnership</h4>
                    <p className="text-gray-600">We don't disappear after launch. Ongoing support and scaling are part of the relationship.</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&h=600&fit=crop"
                  alt="Team collaboration"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 bg-gray-900 p-6 rounded-xl shadow-xl text-white">
                <div className="text-4xl font-bold mb-1">4.9/5</div>
                <div className="text-sm text-gray-300">Average Client Rating</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Case Studies Section */}
      <section className="py-20 lg:py-28 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-end gap-8 mb-16">
            <div className="max-w-2xl">
              <span className="text-gray-500 font-semibold text-sm uppercase tracking-wider">Featured Work</span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-3 mb-4">
                Results That Speak for Themselves
              </h2>
              <p className="text-lg text-gray-600">
                Real projects. Real impact. See how we've helped companies like yours achieve their goals.
              </p>
            </div>
            <Link
              to="/case-studies"
              className="inline-flex items-center gap-2 text-primary font-semibold hover:gap-3 transition-all"
            >
              View All Case Studies
              <span className="material-symbols-outlined">arrow_forward</span>
            </Link>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {caseStudies.map((study, index) => (
              <Link key={index} to="/case-studies" className="group block">
                <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-xl transition-all duration-300">
                  <div className="aspect-video overflow-hidden">
                    <img 
                      src={study.image}
                      alt={study.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-8">
                    <span className="inline-block px-3 py-1 bg-gray-100 text-gray-700 text-sm font-semibold rounded-full mb-4">{study.category}</span>
                    <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-primary transition-colors">{study.title}</h3>
                    <div className="flex items-end gap-2">
                      <span className="text-4xl font-bold text-gray-900">{study.metric}</span>
                      <span className="text-gray-600 mb-1">{study.metricLabel}</span>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-gray-500 font-semibold text-sm uppercase tracking-wider">Testimonials</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-3 mb-4">
              What Our Clients Say
            </h2>
            <p className="text-lg text-gray-600">
              Don't just take our word for it. Here's what the leaders we've worked with have to say.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-gray-50 p-8 rounded-2xl border border-gray-100">
                <div className="flex gap-1 mb-6">
                  {[...Array(5)].map((_, i) => (
                    <span key={i} className="material-symbols-outlined text-yellow-400 text-xl">star</span>
                  ))}
                </div>
                <p className="text-gray-700 leading-relaxed mb-6">"{testimonial.quote}"</p>
                <div className="flex items-center gap-4">
                  <img 
                    src={testimonial.image}
                    alt={testimonial.author}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  <div>
                    <div className="font-semibold text-gray-900">{testimonial.author}</div>
                    <div className="text-sm text-gray-500">{testimonial.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-28 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-5xl font-bold mb-6">
            Ready to Build Something Amazing?
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Let's discuss your project. Schedule a free consultation with our team and discover how we can help you achieve your goals.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-8 py-4 text-base font-semibold text-gray-900 bg-white rounded-xl shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200"
            >
              Get Your Free Consultation
              <span className="material-symbols-outlined text-xl ml-2">arrow_forward</span>
            </Link>
          </div>
          <p className="text-sm text-gray-400 mt-6">No commitment required. We'll respond within 24 hours.</p>
        </div>
      </section>
    </main>
  );
};

export default HomePage;
